<template>
  <div>
    <div class="mb-4 mt-3 flex">
      <v-text-field
        v-model="searchQuery"
        :placeholder="t('beneficiaries.searchPlaceholder')"
        prepend-inner-icon="mdi-magnify"
        clearable
        variant="outlined"
        single-line
        density="compact"
        hide-details
        class="mr-2 grow"
      ></v-text-field>
      <div class="flex gap-2">
        <v-btn
          density="compact"
          height="auto"
          append-icon="mdi-download"
          border
          @click="onDownloadCsv()"
          >{{ t("common.download") }}</v-btn
        >
        <v-btn
          v-if="accountType === 'treezor' && userStore.isKind('user')"
          density="compact"
          height="auto"
          prepend-icon="mdi-plus"
          color="primary"
          variant="flat"
          @click="showAddBeneficiaryDialog = true"
          >{{ t("beneficiaries.add") }}</v-btn
        >
      </div>
    </div>

    <AddBeneficiaryDialog
      v-if="accountType === 'treezor' && userStore.isKind('user')"
      v-model="showAddBeneficiaryDialog"
      :account-id="accountId"
    />

    <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>

    <v-table v-else hover>
      <thead>
        <tr>
          <th
            v-for="header in headers"
            :key="header.key"
            class="text-left font-bold"
            style="cursor: pointer"
            @click="sortBy(header.key)"
          >
            {{ header.label }}
            <v-icon v-if="sortColumn === header.key" size="small" class="ml-1">
              {{ sortDirection === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
            </v-icon>
          </th>
        </tr>
      </thead>
      <tbody>
        <RowLink
          v-for="beneficiary in sortedBeneficiaries"
          :key="beneficiary.iban"
          :to="{
            name: 'BeneficiaryTransactions',
            params: { accountId, beneficiaryIban: beneficiary.iban },
          }"
        >
          <RowLinkCell focusable>
            <v-badge
              v-if="
                pendingTransfersCountByIban.get(beneficiary.iban) &&
                pendingTransfersCountByIban.get(beneficiary.iban)! > 0
              "
              v-tooltip="
                t('beneficiaries.pendingTransfers', {
                  count: pendingTransfersCountByIban.get(beneficiary.iban),
                })
              "
              style="z-index: 1; position: relative"
              color="warning"
              :content="pendingTransfersCountByIban.get(beneficiary.iban)"
              inline
            ></v-badge>
            {{ beneficiary.name }}
          </RowLinkCell>
          <RowLinkCell>{{ beneficiary.iban }}</RowLinkCell>
          <RowLinkCell>{{ beneficiary.totalSentFormatted }}</RowLinkCell>
          <RowLinkCell>{{ beneficiary.totalReceivedFormatted }}</RowLinkCell>
          <RowLinkCell :class="[beneficiary.netAmount > 0 && 'text-primary']">
            {{ beneficiary.netAmountFormatted }}
          </RowLinkCell>
          <RowLinkCell>{{ beneficiary.transactionCount }}</RowLinkCell>
          <RowLinkCell>{{ beneficiary.lastTransactionDateFormatted }}</RowLinkCell>
        </RowLink>
      </tbody>
    </v-table>

    <div v-if="!loading && !sortedBeneficiaries.length" class="text-center py-3">
      {{ t("beneficiaries.noBeneficiaries") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import RowLink from "@/components/RowLink/RowLink.vue";
import RowLinkCell from "@/components/RowLink/RowLinkCell.vue";
import { useAccountCurrency } from "@/composables/useAccountCurrency";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useTransactions } from "@/composables/useTransactions";
import { useTransferRequests } from "@/composables/useTransferRequests";
import { db } from "@/firebase";
import { useUserStore } from "@/store";
import { downloadCsv, formatNumberForCsv } from "@/utils/downloadCsv";
import { formatCurrency } from "@/utils/tools";
import type { Beneficiary } from "@/utils/types";
import AddBeneficiaryDialog from "@/views/Account/views/BeneficiaryDetail/AddBeneficiaryDialog.vue";
import {
  treezorBeneficiariesFullPath,
  type Beneficiary as TreezorBeneficiary,
} from "@aircap/common/treezor";
import { collection, orderBy, query, where } from "firebase/firestore";
import { computed, ref, toRef } from "vue";

const props = defineProps<{
  accountId: string;
}>();

const { t, locale } = useI18n();
const userStore = useUserStore();
const accountId = toRef(props, "accountId");
const searchQuery = ref("");
const showAddBeneficiaryDialog = ref(false);
const sortColumn = ref<keyof Beneficiary>("lastTransactionDate");
const sortDirection = ref<"asc" | "desc">("desc");

const { currencyCode, accountType } = useAccountCurrency(accountId);

const beneficiariesSource = computed(() => {
  switch (accountType.value) {
    case "treezor":
      return query(
        collection<TreezorBeneficiary>(db, treezorBeneficiariesFullPath(accountId.value)),
        where<TreezorBeneficiary>("isActive", "==", true),
        orderBy<TreezorBeneficiary>("createdDate", "desc"),
      );
    case "swan":
    case "bridge":
    case "powens":
    case "gcl":
    case undefined:
      return null;
  }
});
const { data: rawBeneficiaries, pending: loadingBeneficiaries } =
  useCollection<Omit<TreezorBeneficiary, "id">>(beneficiariesSource);

const { transactions, loading: loadingTransactions } = useTransactions(accountId, {
  isFuture: toRef(() => false),
});

const { transferRequests, loadingTransferRequests } = useTransferRequests(accountId, {
  status: toRef(() => "pending"),
});

const loading = computed(
  () => loadingTransactions.value || loadingTransferRequests.value || loadingBeneficiaries.value,
);

const pendingTransfersCountByIban = computed(() => {
  const countMap = new Map<string, number>();

  if (!transferRequests.value) return countMap;

  transferRequests.value.forEach((transferRequest) => {
    if (transferRequest.status === "pending" && transferRequest.beneficiaryIban) {
      const currentCount = countMap.get(transferRequest.beneficiaryIban) || 0;
      countMap.set(transferRequest.beneficiaryIban, currentCount + 1);
    }
  });

  return countMap;
});

const headers = computed(
  () =>
    [
      { key: "name", label: t("beneficiaries.table.name") },
      { key: "iban", label: t("beneficiaries.table.iban") },
      { key: "totalSent", label: t("beneficiaries.table.totalSent") },
      { key: "totalReceived", label: t("beneficiaries.table.totalReceived") },
      { key: "netAmount", label: t("beneficiaries.table.netAmount") },
      {
        key: "transactionCount",
        label: t("beneficiaries.table.transactionCount"),
      },
      {
        key: "lastTransactionDate",
        label: t("beneficiaries.table.lastTransaction"),
      },
    ] satisfies { key: keyof Beneficiary; label: string }[],
);

const beneficiaries = computed<Beneficiary[]>(() => {
  const currencyCodeValue = currencyCode.value;
  if (!currencyCodeValue) return [];

  const beneficiaryMap = new Map<
    string,
    {
      name: string;
      iban: string;
      totalSent: number;
      totalReceived: number;
      transactionCount: number;
      lastTransactionDate: string | null;
    }
  >(
    rawBeneficiaries.value
      .filter(
        (
          beneficiary,
        ): beneficiary is Omit<TreezorBeneficiary, "id"> & {
          id: string;
          metadata: NonNullable<Required<TreezorBeneficiary["metadata"]>>;
        } => !!beneficiary.metadata?.iban,
      )
      .map((beneficiary) => [
        beneficiary.metadata.iban,
        {
          name: beneficiary.name,
          iban: beneficiary.metadata.iban,
          totalSent: 0,
          totalReceived: 0,
          transactionCount: 0,
          lastTransactionDate: null,
        },
      ]),
  );

  if (transactions.value) {
    transactions.value.forEach((transaction) => {
      if (!transaction.counterpartyIban) return;

      const existing = beneficiaryMap.get(transaction.counterpartyIban);
      if (existing) {
        if (transaction.amount < 0) {
          existing.totalSent += Math.abs(transaction.amount);
        } else if (transaction.amount > 0) {
          existing.totalReceived += Math.abs(transaction.amount);
        }
        existing.transactionCount++;

        if (transaction.date > (existing.lastTransactionDate || "")) {
          existing.lastTransactionDate = transaction.date;
        }
      } else {
        beneficiaryMap.set(transaction.counterpartyIban, {
          name: transaction.counterparty || transaction.counterpartyIban,
          iban: transaction.counterpartyIban,
          totalSent: transaction.amount < 0 ? Math.abs(transaction.amount) : 0,
          totalReceived: transaction.amount > 0 ? Math.abs(transaction.amount) : 0,
          transactionCount: 1,
          lastTransactionDate: transaction.date,
        });
      }
    });
  }

  if (transferRequests.value) {
    transferRequests.value.forEach((transferRequest) => {
      if (transferRequest.status === "pending" && transferRequest.beneficiaryIban) {
        const iban = transferRequest.beneficiaryIban;
        if (!beneficiaryMap.has(iban)) {
          beneficiaryMap.set(iban, {
            name: transferRequest.beneficiaryName || iban,
            iban,
            totalSent: 0,
            totalReceived: 0,
            transactionCount: 0,
            lastTransactionDate: null,
          });
        }
      }
    });
  }

  return Array.from(beneficiaryMap.values()).map((beneficiary) => {
    const netAmount = beneficiary.totalReceived - beneficiary.totalSent;

    return {
      name: beneficiary.name,
      iban: beneficiary.iban,
      totalSent: beneficiary.totalSent,
      totalSentFormatted: formatCurrency({
        value: -beneficiary.totalSent,
        currencyCode: currencyCodeValue,
        sign: true,
        locale: locale.value,
      }),
      totalReceived: beneficiary.totalReceived,
      totalReceivedFormatted: formatCurrency({
        value: beneficiary.totalReceived,
        currencyCode: currencyCodeValue,
        sign: true,
        locale: locale.value,
      }),
      netAmount,
      netAmountFormatted: formatCurrency({
        value: netAmount,
        currencyCode: currencyCodeValue,
        sign: true,
        locale: locale.value,
      }),
      transactionCount: beneficiary.transactionCount,
      lastTransactionDate: beneficiary.lastTransactionDate || "",
      lastTransactionDateFormatted: beneficiary.lastTransactionDate
        ? new Intl.DateTimeFormat(locale.value).format(new Date(beneficiary.lastTransactionDate))
        : "-",
    };
  });
});

const filteredBeneficiaries = computed(() => {
  if (!searchQuery.value) return beneficiaries.value;

  const query = searchQuery.value.toLowerCase();
  return beneficiaries.value.filter(
    (beneficiary) =>
      beneficiary.name.toLowerCase().includes(query) ||
      beneficiary.iban.toLowerCase().includes(query),
  );
});

const sortedBeneficiaries = computed(() => {
  const sorted = [...filteredBeneficiaries.value];

  sorted.sort((a, b) => {
    const aValue = a[sortColumn.value];
    const bValue = b[sortColumn.value];

    let comparison = 0;
    if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    } else if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.localeCompare(bValue);
    }

    return sortDirection.value === "asc" ? comparison : -comparison;
  });

  return sorted;
});

const sortBy = (column: keyof Beneficiary) => {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortColumn.value = column;
    sortDirection.value = "desc";
  }
};

const onDownloadCsv = () => {
  const csvHeaders = headers.value.map((h) => h.label);
  const rows = sortedBeneficiaries.value.map((beneficiary) => [
    beneficiary.name,
    beneficiary.iban,
    formatNumberForCsv(-beneficiary.totalSent),
    formatNumberForCsv(beneficiary.totalReceived),
    formatNumberForCsv(beneficiary.netAmount),
    beneficiary.transactionCount.toString(),
    beneficiary.lastTransactionDateFormatted,
  ]);
  downloadCsv([csvHeaders, ...rows], "beneficiaries.csv");
};
</script>
